import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Checkbox } from '@/components/ui/checkbox';
import { LeadRow } from './LeadRow';
import { LeadCard } from './LeadCard';
import { LIST_CONTAINER } from '../lib/motion';
import type { Lead } from '@/types/api';

interface LeadsTableProps {
  leads: Lead[];
  selectedIds: string[];
  allSelected: boolean;
  onToggleAll: (checked: boolean) => void;
  onToggleOne: (id: string, checked: boolean) => void;
  onUpdate: (id: string, updates: Partial<Lead>) => void;
  onDelete: (id: string) => void;
  /**
   * Identity for the current filter/search/page combination. Used as the
   * `key` on the list containers so changing filters remounts the list and
   * replays the stagger-reveal, instead of only staggering once on first load.
   * Deleting a single lead (same key) still animates out via AnimatePresence.
   */
  listKey: string;
}

/** Table on desktop, compact cards on mobile — same data, same interactions. */
export function LeadsTable({
  leads,
  selectedIds,
  allSelected,
  onToggleAll,
  onToggleOne,
  onUpdate,
  onDelete,
  listKey,
}: LeadsTableProps) {
  return (
    <>
      {/* Desktop */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <th className="w-10 px-3 py-2.5">
                <Checkbox checked={allSelected} onChange={(e) => onToggleAll(e.target.checked)} aria-label="Select all leads" />
              </th>
              <th className="px-3 py-2.5">Lead</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Priority</th>
              <th className="px-3 py-2.5">Source</th>
              <th className="px-3 py-2.5">Received</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <motion.tbody key={listKey} variants={LIST_CONTAINER} initial="hidden" animate="show">
            <AnimatePresence>
              {leads.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  isSelected={selectedIds.includes(lead.id)}
                  onSelect={(checked) => onToggleOne(lead.id, checked)}
                  onUpdate={(updates) => onUpdate(lead.id, updates)}
                  onDelete={() => onDelete(lead.id)}
                />
              ))}
            </AnimatePresence>
          </motion.tbody>
        </table>
      </div>

      {/* Mobile */}
      <motion.div
        key={listKey}
        variants={LIST_CONTAINER}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 gap-2 p-2 md:hidden"
      >
        <AnimatePresence>
          {leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              isSelected={selectedIds.includes(lead.id)}
              onSelect={(checked) => onToggleOne(lead.id, checked)}
              onUpdate={(updates) => onUpdate(lead.id, updates)}
              onDelete={() => onDelete(lead.id)}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </>
  );
}
